import { getCurrencyDecimals } from './currency-decimals';

/**
 * Format a major-unit money amount with currency-specific precision.
 *
 * R22-3: Replaces hardcoded `.toFixed(2)` calls on major-unit values.
 * Precision is sourced from the currency code (see `currency-decimals.ts`).
 *
 * @param value - amount in major units (e.g. `19.99` EUR, `0.00010000` BTC).
 * @param currency - currency code used for precision lookup.
 * @param decimalsOverride - explicit precision (wins over registry lookup).
 */
export function formatAmount(
  value: number,
  currency: string,
  decimalsOverride?: number
): string {
  const decimals =
    typeof decimalsOverride === 'number' && Number.isFinite(decimalsOverride) && decimalsOverride >= 0
      ? Math.floor(decimalsOverride)
      : getCurrencyDecimals(currency);

  const safeDecimals = Math.min(decimals, 20);

  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: safeDecimals,
    maximumFractionDigits: safeDecimals,
    useGrouping: false,
  }).format(value);
}

/**
 * Format a MINOR-unit money amount (e.g. cents, satoshi) with currency-specific
 * precision. The crash-game store carries `balance` as minor units internally,
 * so the display path needs to divide by the correct `10^decimals` rather than
 * the hardcoded `/ 100` that was in place before R22-3.
 *
 * For a BTC balance of `10_000` (satoshi), this returns `"0.00010000"`.
 * For a EUR balance of `1_999` (cents), this returns `"19.99"`.
 *
 * @param minor - amount in minor units (integer number or bigint-like string).
 * @param currency - currency code used for precision lookup.
 * @param decimalsOverride - explicit precision (wins over registry lookup).
 */
export function formatMinorUnits(
  minor: number | string,
  currency: string,
  decimalsOverride?: number
): string {
  const decimals =
    typeof decimalsOverride === 'number' && Number.isFinite(decimalsOverride) && decimalsOverride >= 0
      ? Math.floor(decimalsOverride)
      : getCurrencyDecimals(currency);

  const safeDecimals = Math.min(decimals, 20);

  // Use bigint to avoid float-precision drift on large crypto values.
  let big: bigint;
  if (typeof minor === 'string') {
    // Strip any decimal portion — the contract is minor units (integer).
    const intPart = minor.split('.')[0]?.replace(/[^0-9-]/g, '') ?? '0';
    big = BigInt(intPart === '' || intPart === '-' ? '0' : intPart);
  } else {
    big = BigInt(Math.trunc(minor));
  }

  const divisor = BigInt(10) ** BigInt(safeDecimals);
  const negative = big < 0n;
  const abs = negative ? -big : big;
  const whole = abs / divisor;
  const frac = (abs % divisor).toString().padStart(safeDecimals, '0');
  const sign = negative ? '-' : '';
  const asNumberString = safeDecimals > 0 ? `${whole.toString()}.${frac}` : whole.toString();

  // Convert back through Intl for locale-aware grouping/decimal separator.
  const asNumber = Number(asNumberString);
  if (Number.isFinite(asNumber)) {
    return (
      sign +
      new Intl.NumberFormat(undefined, {
        minimumFractionDigits: safeDecimals,
        maximumFractionDigits: safeDecimals,
        useGrouping: false,
      }).format(asNumber)
    );
  }

  // Fallback for ETH-scale values where Number loses precision:
  return sign + asNumberString;
}
